import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FaEnvelope, FaPhoneAlt, FaUser, FaUserPlus } from "react-icons/fa";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { Button } from "@components/buttons/button";
import { Close } from "@components/buttons/close";
import { Checkbox } from "@components/forms/checkbox";
import { Input } from "@components/forms/input";
import { InputGroup } from "@components/forms/input-group";
import { Select } from "@components/forms/select";
import { useAuthContext } from "@hooks/contexts/auth-context";
import { useModalContext } from "@hooks/contexts/modal-context";
import { genderTypes } from "@hooks/functions/Customer/gender";
import { config } from "config";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
}

interface IFormValues {
  gender?: number | null;
  userName: string;
  atSign: string;
  name: string;
  password: string;
  passwordConfirm: string;
  email: string;
  document?: string;
  cellPhone: string;
  termsCheck: boolean;
  birth: string;
  language: string;
  referral?: number;
}

const genderOptions = genderTypes
  .filter((gender) => gender.id > 0 && gender.id <= 6)
  .map((gender) => ({
    value: gender.id,
    name: gender.name,
  }));

function normalizeAtSign(value: string) {
  return value
    .toLowerCase()
    .replace(/@/g, "")
    .replace(/[^a-z0-9._]/g, "");
}

const availabilityMessages: Record<string, string> = {
  email: "Este e-mail já está cadastrado.",
  userName: "Este nome de usuário já está cadastrado.",
  document: "Este documento já está cadastrado.",
  atSign: "Este @atSign já está cadastrado.",
};

export function ModalRegister({ isOpen, onClose }: IProps) {
  const [animate, setAnimate] = useState(false);
  const [loading, setLoading] = useState(false);

  const { changeHash } = useModalContext();
  const { checkCustomerExists, createCustomer } = useAuthContext();

  const scheme = yup.object().shape({
    name: yup
      .string()
      .required("Nome é obrigatório.")
      .test(
        "FullNameTest",
        "Digite seu nome completo.",
        (value) => !!value && value.trim().split(" ").length > 1,
      ),
    userName: yup
      .string()
      .required("Nome de usuário é obrigatório.")
      .min(3, "Informe pelo menos 3 caracteres."),
    atSign: yup
      .string()
      .required("@atSign é obrigatório.")
      .matches(
        /^[a-z0-9._]+$/,
        "Use apenas letras minúsculas, números, ponto e underline.",
      ),
    email: yup
      .string()
      .required("E-mail é obrigatório.")
      .email("E-mail inválido."),
    document: yup.string().optional(),
    cellPhone: yup.string().required("Whatsapp é obrigatório."),
    birth: yup.string().required("Data de nascimento é obrigatória."),
    gender: yup
      .number()
      .nullable()
      .transform((value) => (Number.isNaN(value) ? null : value)),
    password: yup
      .string()
      .required("Senha é obrigatória.")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
        "Use letras maiúsculas, minúsculas, números, caracteres especiais e no mínimo 8 caracteres.",
      ),
    passwordConfirm: yup
      .string()
      .required("Confirmação de senha é obrigatória.")
      .test("SamePassword", "As senhas não coincidem.", function (value) {
        return value === this.parent.password;
      }),
    termsCheck: yup
      .bool()
      .oneOf([true], "Você deve aceitar o acordo do usuário."),
  });

  const {
    register,
    reset,
    setError,
    clearErrors,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormValues>({
    resolver: yupResolver(scheme) as any,
    mode: "onSubmit",
    reValidateMode: "onBlur",
    defaultValues: {
      language: "pt-br",
      gender: null,
      atSign: "",
    },
  });

  const handleClose = useCallback(() => {
    setAnimate(false);
    setTimeout(() => onClose(), 300);
  }, [onClose]);

  const handleEscape = useCallback(
    (evt: KeyboardEvent) => {
      if (evt.key === "Escape" || evt.key === "Esc" || evt.keyCode === 27) {
        handleClose();
      }
    },
    [handleClose],
  );

  function syncAutofillValues(form: HTMLFormElement) {
    const fields: Array<keyof IFormValues> = [
      "name",
      "userName",
      "atSign",
      "email",
      "document",
      "cellPhone",
      "birth",
      "gender",
      "password",
      "passwordConfirm",
    ];

    fields.forEach((field) => {
      const element = form.elements.namedItem(field);
      if (
        element instanceof HTMLInputElement ||
        element instanceof HTMLSelectElement
      ) {
        const value =
          field === "atSign" ? normalizeAtSign(element.value) : element.value;
        setValue(field, value as never, { shouldDirty: true });
      }
    });

    const terms = form.elements.namedItem("termsCheck");
    if (terms instanceof HTMLInputElement) {
      setValue("termsCheck", terms.checked, { shouldDirty: true });
    }
  }
  async function handleRegister(data: IFormValues) {
    setLoading(true);
    clearErrors(["email", "userName", "document", "atSign"]);

    const atSign = normalizeAtSign(data.atSign);
    const exists = await checkCustomerExists({
      email: data.email,
      userName: data.userName,
      document: data.document,
      atSign,
    });

    if (!exists) {
      setLoading(false);
      return;
    }

    const unavailableFields = Object.entries(exists).filter(
      ([field, value]) => value === true && availabilityMessages[field],
    );

    if (unavailableFields.length > 0) {
      unavailableFields.forEach(([field]) => {
        setError(field as keyof IFormValues, {
          type: "unavailable",
          message: availabilityMessages[field],
        });
      });
      setLoading(false);
      return;
    }

    await createCustomer({
      ...data,
      atSign,
      emailConfirm: data.email,
      language: "pt-br",
      termsCheck: data.termsCheck ? "y" : "n",
      gender: data.gender ? Number(data.gender) : undefined,
    });
    setLoading(false);
  }

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimate(true), 10);
      document.addEventListener("keydown", handleEscape);
      reset();
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    }

    setAnimate(false);
  }, [handleEscape, isOpen, reset]);

  return (
    <>
      <div
        className={`modal fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
        aria-labelledby="registerModalLabel"
        aria-hidden={!isOpen}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-register-content modal-content border-0 rounded-4 overflow-hidden shadow-lg">
            <div
              className="modal-header border-0 px-4 px-lg-5 pt-4 pb-0"
              style={{
                background: "linear-gradient(135deg, #071423 0%, #0d1f35 100%)",
              }}
            >
              <div>
                <span className="badge bg-primary text-white rounded-pill px-3 py-2 mb-3">
                  Criar conta
                </span>
                <h4
                  className="modal-title fw-bold text-white"
                  id="registerModalLabel"
                >
                  Cadastre-se na {config.appName}
                </h4>
                <p className="text-white-50 mb-0">
                  Informe seus dados para acessar campanhas, vendas e comissões.
                </p>
              </div>
              <Close onPress={handleClose} />
            </div>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                syncAutofillValues(event.currentTarget);
                handleSubmit(handleRegister)();
              }}
            >
              <div className="modal-body px-4 px-lg-5 py-4">
                <div className="row g-3">
                  <div className="col-md-6">
                    <Input
                      label="Nome completo"
                      type="text"
                      register={register("name")}
                      placeholder="João Silva"
                      errorMessage={errors?.name?.message}
                    />
                  </div>

                  <div className="col-md-6">
                    <InputGroup
                      label="Nome de usuário"
                      type="text"
                      register={register("userName")}
                      icon={<FaUser />}
                      placeholder="joao.silva"
                      errorMessage={errors?.userName?.message}
                    />
                  </div>

                  <div className="col-md-6">
                    <InputGroup
                      label="@atSign"
                      type="text"
                      register={register("atSign")}
                      icon={<FaUser />}
                      placeholder="joao.silva"
                      errorMessage={errors?.atSign?.message}
                      onChange={(event) => {
                        const value = normalizeAtSign(event.target.value);
                        event.target.value = value;
                        setValue("atSign", value, { shouldValidate: true });
                      }}
                    />
                    <small className="text-muted">
                      Será usado sem o @ no seu perfil.
                    </small>
                  </div>

                  <div className="col-md-6">
                    <InputGroup
                      label="E-mail"
                      type="email"
                      register={register("email")}
                      icon={<FaEnvelope />}
                      placeholder="joao@email.com"
                      errorMessage={errors?.email?.message}
                    />
                  </div>

                  <div className="col-md-6">
                    <Input
                      label="CPF"
                      type="text"
                      register={register("document")}
                      mask="cpf"
                      placeholder="000.000.000-00"
                      errorMessage={errors?.document?.message}
                    />
                  </div>

                  <div className="col-md-6">
                    <InputGroup
                      label="Whatsapp"
                      type="text"
                      register={register("cellPhone")}
                      mask="phone"
                      icon={<FaPhoneAlt />}
                      placeholder="99 99999-9999"
                      errorMessage={errors?.cellPhone?.message}
                    />
                  </div>

                  <div className="col-md-6">
                    <Input
                      label="Data de nascimento"
                      type="date"
                      register={register("birth")}
                      errorMessage={errors?.birth?.message}
                    />
                  </div>

                  <div className="col-md-6">
                    <Select
                      label="Gênero"
                      register={register("gender")}
                      options={genderOptions}
                      errorMessage={errors?.gender?.message as string}
                    />
                  </div>

                  <div className="col-md-6">
                    <InputGroup
                      label="Senha"
                      type="password"
                      register={register("password")}
                      placeholder="********"
                      errorMessage={errors?.password?.message}
                    />
                  </div>

                  <div className="col-md-6">
                    <InputGroup
                      label="Confirmar senha"
                      type="password"
                      register={register("passwordConfirm")}
                      placeholder="********"
                      errorMessage={errors?.passwordConfirm?.message}
                    />
                  </div>

                  <div className="col-12">
                    <div className="border rounded-4 p-3 bg-light">
                      <Checkbox
                        label="Eu aceito o acordo do usuário e confirmo que tenho pelo menos 18 anos."
                        id="termsCheck"
                        register={register("termsCheck")}
                        errorMessage={errors?.termsCheck?.message}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer border-0 px-4 px-lg-5 pb-4 pt-0">
                <div className="d-flex flex-column-reverse flex-sm-row justify-content-end gap-2 w-100">
                  <Button
                    type="submit"
                    className="btn btn-primary text-white rounded-4 py-3 flex-fill"
                    loading={loading}
                  >
                    <FaUserPlus className="fs-5 me-2" />
                    Cadastrar
                  </Button>

                  <Button
                    type="button"
                    className="btn btn-outline-secondary rounded-4 py-3 flex-fill"
                    onClick={() => changeHash("")}
                  >
                    Fechar
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div
        className={`modal-backdrop fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
      />
    </>
  );
}
